// ---------------------------------------------------------------------------
// The three panels that act on a person: the ban list, the report queue, and
// the force-rename form.
//
// Split out of `AdminScreen.tsx`. They are together because they are one
// workflow read three ways — a report names a subject, a ban takes one, and a
// rename is what is done instead of a ban when the name is the whole problem.
// `Reports` and `RoomDetail` both fill the ban form rather than banning
// directly, which is why the draft lives on the screen and arrives here as a
// prop: the admin sees what is about to be banned before it happens.
// ---------------------------------------------------------------------------

import { useState } from 'react';
import type { AdminBan, AdminReport, BanScope } from '@countdown/shared';
import { Button, Card, CardTitle } from '../../components/primitives/index.js';
import { ConfirmAction, clockTime } from './atoms.js';
import type { BanDraft } from './atoms.js';

export function Bans({
  bans,
  draft,
  onDraft,
  onAdd,
  onLift,
}: {
  bans: AdminBan[];
  draft: BanDraft;
  onDraft: (draft: BanDraft) => void;
  onAdd: () => void;
  onLift: (id: number) => void;
}) {
  return (
    <Card>
      <CardTitle>Bans</CardTitle>

      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <label className="flex flex-col gap-1.5">
          <span className="label-mono">subject kind</span>
          <select
            value={draft.subjectKind}
            onChange={(event) =>
              onDraft({ ...draft, subjectKind: event.target.value as BanDraft['subjectKind'] })
            }
            className="min-h-11 rounded-card border border-line bg-surface-2 px-3 text-[16px] text-ink"
          >
            <option value="ip">ip</option>
            <option value="device">device</option>
            <option value="user">user</option>
          </select>
        </label>
        <label className="flex flex-1 flex-col gap-1.5">
          <span className="label-mono">subject</span>
          <input
            value={draft.subject}
            onChange={(event) => onDraft({ ...draft, subject: event.target.value })}
            className="min-h-11 w-full rounded-card border border-line bg-surface-2 px-3 font-mono text-[16px] text-ink"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="label-mono">scope</span>
          <select
            value={draft.scope}
            onChange={(event) => onDraft({ ...draft, scope: event.target.value as BanScope })}
            className="min-h-11 rounded-card border border-line bg-surface-2 px-3 text-[16px] text-ink"
          >
            <option value="global">global</option>
            <option value="room">room</option>
          </select>
        </label>
        {draft.scope === 'room' && (
          <label className="flex flex-col gap-1.5">
            <span className="label-mono">room</span>
            <input
              value={draft.roomCode}
              onChange={(event) => onDraft({ ...draft, roomCode: event.target.value })}
              className="min-h-11 w-[110px] rounded-card border border-line bg-surface-2 px-3 font-mono text-[16px] text-ink uppercase"
            />
          </label>
        )}
        <label className="flex flex-1 flex-col gap-1.5">
          <span className="label-mono">reason</span>
          <input
            value={draft.reason}
            onChange={(event) => onDraft({ ...draft, reason: event.target.value })}
            className="min-h-11 w-full rounded-card border border-line bg-surface-2 px-3 text-[16px] text-ink"
          />
        </label>
        <ConfirmAction
          label="Add ban"
          question={
            draft.subject.trim()
              ? `Ban ${draft.subjectKind} ${draft.subject.trim()}?`
              : 'Nothing to ban until a subject is filled in.'
          }
          confirmLabel="Yes, add the ban"
          onConfirm={onAdd}
        />
      </div>

      {bans.length === 0 ? (
        <p className="mt-4 text-sm text-ink-dim">Nobody is banned.</p>
      ) : (
        <ul className="mt-4 flex flex-col divide-y divide-line">
          {bans.map((ban) => (
            <li key={ban.id} className="flex flex-wrap items-center gap-3 py-3">
              <span className="font-mono text-sm">
                {ban.subjectKind} {ban.subject}
              </span>
              <span className="text-sm text-ink-muted">
                {ban.scope === 'room' ? `room ${ban.roomCode}` : 'global'}
                {ban.reason ? ` · ${ban.reason}` : ''}
              </span>
              <span className="font-mono text-xs text-ink-dim">
                {ban.expiresAt ? `until ${clockTime(ban.expiresAt)}` : 'no expiry'}
              </span>
              <div className="ml-auto">
                <Button variant="outline" size="sm" onClick={() => onLift(ban.id)}>
                  Lift
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function Reports({
  reports,
  onResolve,
  onBanSubject,
}: {
  reports: AdminReport[];
  onResolve: (id: number) => void;
  onBanSubject: (draft: BanDraft) => void;
}) {
  const open = reports.filter((report) => !report.resolved);

  return (
    <Card>
      <CardTitle>Reports</CardTitle>
      {open.length === 0 ? (
        <p className="mt-3 text-sm text-ink-dim">The queue is empty.</p>
      ) : (
        <ul className="mt-3 flex flex-col divide-y divide-line">
          {open.map((report) => (
            <li key={report.id} className="flex flex-col gap-1.5 py-3">
              <p className="text-sm">
                <span className="font-medium">{report.subjectLabel}</span> in {report.roomCode},
                reported by {report.reporterLabel} at {clockTime(report.at)}
              </p>
              <p className="text-sm text-ink-muted">{report.reason}</p>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => onResolve(report.id)}>
                  Resolve
                </Button>
                {report.subjectUserId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      onBanSubject({
                        subjectKind: 'user',
                        subject: report.subjectUserId ?? '',
                        scope: 'global',
                        roomCode: '',
                        reason: report.reason,
                      })
                    }
                  >
                    Fill ban from account
                  </Button>
                )}
                {report.subjectDeviceId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      onBanSubject({
                        subjectKind: 'device',
                        subject: report.subjectDeviceId ?? '',
                        scope: 'global',
                        roomCode: '',
                        reason: report.reason,
                      })
                    }
                  >
                    Fill ban from device
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function ForceRenameUser({
  onRename,
}: {
  onRename: (userId: string, includeUsername: boolean) => void;
}) {
  const [userId, setUserId] = useState('');
  const [includeUsername, setIncludeUsername] = useState(false);

  return (
    <Card>
      <CardTitle>Force rename an account</CardTitle>
      <p className="mt-2 max-w-[520px] text-sm text-ink-muted text-pretty">
        Resets the display name. Ticking the username resets the permanent handle
        too, which every old profile link then points at nothing.
      </p>
      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <label className="flex flex-1 flex-col gap-1.5">
          <span className="label-mono">user id</span>
          <input
            value={userId}
            onChange={(event) => setUserId(event.target.value)}
            className="min-h-11 w-full rounded-card border border-line bg-surface-2 px-3 font-mono text-[16px] text-ink"
          />
        </label>
        <label className="flex min-h-11 items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeUsername}
            onChange={(event) => setIncludeUsername(event.target.checked)}
            className="size-5"
          />
          reset the username too
        </label>
        <ConfirmAction
          label="Force rename"
          question={
            includeUsername
              ? 'Reset the display name and the permanent username?'
              : 'Reset the display name?'
          }
          confirmLabel="Yes, rename"
          onConfirm={() => {
            const trimmed = userId.trim();
            if (trimmed) {
              onRename(trimmed, includeUsername);
              setUserId('');
            }
          }}
        />
      </div>
    </Card>
  );
}
